/**
 * DuoInviteCTA - High-emotion invite partner button for Duo tab
 * Shows in all Duo teaser states when user has no partner
 */
import { useState } from "react";
import { Heart, Send, Loader2, Copy, Check, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import { shareToApp } from "@/hooks/useNativeShare";

export default function DuoInviteCTA({ compact = false }) {
  const { token, user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [copied, setCopied] = useState(false);

  const createInvite = async () => {
    if (!token) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/invite/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteData(res.data);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invite");
      return null;
    } finally {
      setCreating(false);
    }
  };

  const handleShareWhatsApp = async () => {
    const data = inviteData || await createInvite();
    if (!data) return;
    const url = `${window.location.origin}${data.invite_url}`;
    const name = user?.name || "Someone";
    const text = `🎉 You've been invited by ${name} to plan adventures together on DuoLyst! Tap to join:`;
    shareToApp("whatsapp", text, url);
  };

  const handleShareGeneric = async () => {
    const data = inviteData || await createInvite();
    if (!data) return;
    const url = `${window.location.origin}${data.invite_url}`;
    const name = user?.name || "Someone";
    const text = `🎉 You've been invited by ${name} to plan adventures together on DuoLyst!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `${name} invited you to DuoLyst`, text, url });
      } catch (e) {
        if (e.name !== "AbortError") handleCopy(url);
      }
    } else {
      handleCopy(url);
    }
  };

  const handleCopy = async (url) => {
    const data = inviteData || await createInvite();
    if (!data && !url) return;
    const link = url || `${window.location.origin}${data.invite_url}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied!");
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      toast.error("Failed to copy link");
    }
  };

  if (compact) {
    return (
      <Button
        onClick={handleShareWhatsApp}
        disabled={creating}
        className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 text-white"
        data-testid="invite-partner-btn"
      >
        {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
        Invite Your Travel Partner
      </Button>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/20" data-testid="duo-invite-cta">
      <CardContent className="p-4 text-center">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mx-auto mb-3">
          <Heart className="w-6 h-6 text-white" />
        </div>
        <h3 className="font-serif text-base font-semibold text-foreground mb-1">
          Build your world together
        </h3>
        <p className="text-xs text-muted-foreground mb-4 px-2">
          Invite your partner to see your shared heatmap, compatibility score, and replay.
        </p>

        <div className="space-y-2">
          <Button
            onClick={handleShareWhatsApp}
            disabled={creating}
            className="w-full h-10 bg-green-500 hover:bg-green-600 text-white font-medium rounded-xl text-sm"
            data-testid="invite-whatsapp-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
            Share on WhatsApp
          </Button>

          <div className="flex gap-2">
            <Button
              onClick={handleShareGeneric}
              disabled={creating}
              variant="outline"
              className="flex-1 rounded-xl text-xs"
              data-testid="invite-share-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              Share
            </Button>
            <Button
              onClick={() => handleCopy()}
              disabled={creating}
              variant="outline"
              className="flex-1 rounded-xl text-xs"
              data-testid="invite-copy-btn"
            >
              {copied ? <Check className="w-3.5 h-3.5 mr-1 text-green-500" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? "Copied" : "Copy Link"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
